import { useQuery } from '@tanstack/react-query';

import { generateCppInvoice } from '~/services/LuxLoyaltyService';
import type { CppInvoiceQuery } from '~/services/LuxLoyaltyService.types';

import { costPerPointQueryKeys } from './costPerPointQueryKeys';

/** How long a fetched invoice stays fresh. Long enough to survive tab switching around a review. */
const INVOICE_STALE_TIME_MS = 5 * 60 * 1000;

interface UseCppInvoiceOptions {
  enabled?: boolean;
}

/** Parameters a caller supplies; `mode` is fixed by the hook. */
type PreviewQuery = Partial<Omit<CppInvoiceQuery, 'mode'>>;

/**
 * Fetches the CPP invoice for one partner over one period, in `preview` mode.
 *
 * A fully and coherently cached period is served straight from its stored costs without repricing,
 * returning exactly what recomputing would; anything else is computed and then persisted whole. So
 * repeatedly viewing an invoice is stable and cheap. Discarding the stored costs and pricing the
 * period again is a deliberate, separate action — see `useRefreshCppInvoice`.
 *
 * Disabled until a partner and both dates are known, so opening the tab does not fire a request
 * for an empty partner. `retry: false` matches the other CPP hooks: a 400 from an invalid range
 * or a 404 from an unknown partner is a permanent answer, not a blip worth retrying.
 */
export function useCppInvoice(query: PreviewQuery, options: UseCppInvoiceOptions = {}) {
  const { enabled = true } = options;
  const isComplete = Boolean(query.partnerId && query.start && query.end);
  const previewQuery = { ...query, mode: 'preview' } as CppInvoiceQuery;

  return useQuery({
    queryKey: costPerPointQueryKeys.invoice(previewQuery),
    queryFn: async ({ signal }) => {
      return await generateCppInvoice(previewQuery, { signal });
    },
    enabled: enabled && isComplete,
    // This "query" is a POST that persists costs for an uncached period, so an incidental refetch
    // is not free. Holding it fresh stops a window refocus repricing a period behind the user's
    // back; recomputing on demand is what Force refresh is for.
    staleTime: INVOICE_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    retry: false,
  });
}
